/**
 * frontend/src/services/ApiService.js
 *
 * All HTTP communication with the Vakya API.
 * Revealing Module Pattern — private implementation, clean public interface.
 *
 * No fetch() calls outside this module.
 * No business logic here — transport only.
 */

const ApiService = (() => {

    // ── Private state ────────────────────────────────────────────────────────

    let _baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    let _sessionId = null;
    let _defaultTimeout = 30000;  // ms
    let _synthesisTimeout = 60000; // ms — Claude needs more time

    // ── Private helpers ──────────────────────────────────────────────────────

    function _getSessionId() {
        if (!_sessionId) {
            _sessionId = sessionStorage.getItem('vakya_session') || _generateId();
            sessionStorage.setItem('vakya_session', _sessionId);
        }
        return _sessionId;
    }

    function _generateId() {
        return 'vs_' + Math.random().toString(36).slice(2, 11);
    }

    async function _request(method, path, body = null, timeout = null) {
        const controller = new AbortController();
        const ms = timeout || _defaultTimeout;
        const timeoutId = setTimeout(() => controller.abort(), ms);

        const headers = {
            'Content-Type': 'application/json',
            'X-Session-Id': _getSessionId(),
        };

        const options = { method, headers, signal: controller.signal };
        if (body) options.body = JSON.stringify(body);

        try {
            const response = await fetch(`${_baseUrl}${path}`, options);
            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new ApiError(
                    error.detail || `HTTP ${response.status}`,
                    response.status
                );
            }

            return response.json();
        } catch (err) {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') throw new ApiError('Request timed out', 408);
            throw err;
        }
    }

    // ── Public interface ─────────────────────────────────────────────────────

    async function search(params) {
        return _request('POST', '/search', params);
    }

    async function synthesize({ question, lens = 'advaita', depth = 'standard' }) {
        /**
         * Calls the Sūta Oracle synthesis endpoint.
         * question: string — philosophical question in any language
         * lens: 'advaita' | 'vishishtadvaita' | 'dvaita' | 'shaiva'
         * depth: 'standard' (haiku, ~3s) | 'deep' (sonnet, ~7s)
         * returns: SynthesizeResponse
         */
        return _request('POST', '/synthesize', { question, lens, depth }, _synthesisTimeout);
    }

    async function getVerse(verseId) {
        return _request('GET', `/verse/${encodeURIComponent(verseId)}`);
    }

    async function listSources() {
        return _request('GET', '/source');
    }

    async function listSourceVerses(sourceCode, limit = 20, cursor = null) {
        const params = new URLSearchParams({ limit });
        if (cursor) params.set('cursor', cursor);
        return _request('GET', `/source/${encodeURIComponent(sourceCode)}/verses?${params}`);
    }
    async function getSourceCount(sourceCode) {
        return _request('GET', `/source/${encodeURIComponent(sourceCode)}/count`);
    }
    async function keywordSearch(query, sourceCodes = null, limit = 20, cursor = null) {
        return _request('POST', '/keyword', { query, source_codes: sourceCodes, limit, cursor });
    }


    async function submitFeedback(queryId, verseId, relevant, comment = null) {
        return _request('POST', '/search/feedback', {
            query_id: queryId,
            verse_id: verseId,
            relevant,
            comment,
        });
    }

    async function health() {
        return _request('GET', '/health');
    }

    function configure(options = {}) {
        if (options.baseUrl)  _baseUrl = options.baseUrl;
        if (options.timeout)  _defaultTimeout = options.timeout;
    }

    async function getSuggestions(q = '', limit = 8) {
        const params = new URLSearchParams({ limit });
        if (q) params.set('q', q);
        return _request('GET', `/suggestions?${params}`);
    }

    async function getCorpusStats() {
        return _request('GET', '/corpus/stats');
    }

    return { search, synthesize, getVerse, listSources, listSourceVerses, getSourceCount, keywordSearch, submitFeedback, health, configure, getCorpusStats, getSuggestions };

})();

// ── Error class ──────────────────────────────────────────────────────────────

export class ApiError extends Error {
    constructor(message, statusCode) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
    }
}

export default ApiService;
